import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { DatePickerValue, F0DatePicker, F0Dialog, F0Select } from '@factorialco/f0-react'
import { Input, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Save } from '@factorialco/f0-react/icons/app'
import {
  AtsOfferLetterFromTemplateContractTypesDocument,
  AtsOfferLetterFromTemplateTeamsDocument,
  AtsOfferLetterTemplateDataToFillFormDocument,
  AtsSendOfferLetterGenerateFileFromVariablesDocument,
  AtsSendOfferLetterGeneratedFileFragment,
  AtsSendOfferLetterGetTemplatePreviewDocument,
  AtsSendOfferLetterGetTemplatesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { PdfViewer } from 'modules/ats/components/PdfViewer'

import { OfferLetterFromTemplateFilePreview } from './PreviewContent'
import { getSalaryWarningStatus } from './lib/getSalaryWarningStatus'
import { getSystemVariableDefaults } from './lib/getSystemVariableDefaults'

interface FormValues {
  templateId: number
  filename: string
}

function uiDateStringToDatePickerValue(
  dateString: string | undefined
): DatePickerValue | undefined {
  if (!dateString) return undefined
  const parsed = stringToDate(dateString, 'ui')
  if (!parsed.isValid()) return undefined
  const d = parsed.toDate()
  return { granularity: 'day', value: { from: d, to: d } }
}

function datePickerValueToUiString(value: DatePickerValue | undefined): string {
  const from = value?.value?.from
  if (!from) return ''
  return i18nDateToString(from, 'ui')
}

interface Props {
  jobApplicationId: string
  onClose: () => void
  onSuccess: () => void
  onSaveForLater?: () => void
}

export const OfferLetterFromTemplateModal = ({
  jobApplicationId,
  onClose,
  onSuccess,
  onSaveForLater,
}: Props) => {
  const { company, employee, email } = useCurrentSession()
  const [content, setContent] = useState<'form' | 'preview'>('form')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [finalPreviewFile, setFinalPreviewFile] =
    useState<AtsSendOfferLetterGeneratedFileFragment | null>(null)
  const [variableValues, setVariableValues] = useState<Record<string, string>>({})
  const [salaryTouched, setSalaryTouched] = useState(false)

  const handleVariableChange = (name: string, value: string) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateId(value)
    form.setFieldValue('templateId', Number(value))
  }

  const templatesSource = useGraphqlDataSource({
    document: AtsSendOfferLetterGetTemplatesDocument,
    paginationType: 'infinite-scroll',
    perPage: 50,
    mapVariables: ({ search, pagination }) => ({
      search: search || undefined,
      first: pagination?.perPage ?? 50,
    }),
    mapData: (data) =>
      data.ats.offerLetterTemplatesConnection.nodes.map((t) => ({
        id: t.id.toString(),
        label: t.name,
        value: t.id.toString(),
      })),
    mapPagination: (data) => ({
      total: data.ats.offerLetterTemplatesConnection.totalCount ?? 0,
      cursor: data.ats.offerLetterTemplatesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.ats.offerLetterTemplatesConnection.pageInfo.hasNextPage,
    }),
  })
  const { data: previewData, loading: isPreviewLoading } = useQuery(
    AtsSendOfferLetterGetTemplatePreviewDocument,
    {
      variables: { id: String(selectedTemplateId) },
      skip: !selectedTemplateId,
    }
  )
  const { data: applicationData } = useQuery(AtsOfferLetterTemplateDataToFillFormDocument, {
    variables: { jobApplicationId },
  })

  const customVariables = previewData?.ats.offerLetterTemplate?.customVariables ?? []
  const systemVariables = useMemo(
    () => previewData?.ats.offerLetterTemplate?.systemVariables ?? [],
    [previewData?.ats.offerLetterTemplate?.systemVariables]
  )

  const needsContractType = systemVariables.some((v) => v.name === 'job_contract_type')
  const needsTeam = systemVariables.some((v) => v.name === 'team_name')

  const { data: contractTypesData, loading: contractTypesLoading } = useQuery(
    AtsOfferLetterFromTemplateContractTypesDocument,
    { skip: !needsContractType }
  )

  const { data: teamsData, loading: teamsLoading } = useQuery(
    AtsOfferLetterFromTemplateTeamsDocument,
    { skip: !needsTeam }
  )

  const contractTypeOptions = useMemo(
    () =>
      contractTypesData?.ats.contractTypesConnection.nodes.map((node) => ({
        label: node.label,
        value: node.label,
      })) ?? [],
    [contractTypesData]
  )

  const workplaceTypeOptions = useMemo(
    () => [
      { label: i18n.t('ats.workplace_types.onsite'), value: i18n.t('ats.workplace_types.onsite') },
      { label: i18n.t('ats.workplace_types.hybrid'), value: i18n.t('ats.workplace_types.hybrid') },
      { label: i18n.t('ats.workplace_types.remote'), value: i18n.t('ats.workplace_types.remote') },
    ],
    []
  )

  const teamOptions = useMemo(
    () =>
      teamsData?.teams.teamsConnection.edges.map((edge) => ({
        label: edge.node.name,
        value: edge.node.name,
      })) ?? [],
    [teamsData]
  )

  const [generateFileFromVariables] = useMutation(
    AtsSendOfferLetterGenerateFileFromVariablesDocument,
    {
      extractorFn: (data) => data.atsMutations.createFromVariablesAtsFile,
    }
  )

  const previewUrl = previewData?.ats.offerLetterTemplate?.atsFile?.preview?.url ?? ''
  const pdfFilename = previewData?.ats.offerLetterTemplate?.atsFile?.preview?.filename ?? ''

  useEffect(() => {
    if (!selectedTemplateId) return

    const variableNames = systemVariables.map((v) => v.name)
    const defaults = getSystemVariableDefaults(variableNames, applicationData?.ats.application, {
      company,
      employee,
      email: email ?? '',
    })
    setVariableValues(defaults)
  }, [selectedTemplateId, systemVariables, applicationData, company, employee, email])

  const form = useForm<FormValues>({
    defaultValues: {
      templateId: 0,
      filename: '',
    },
    validate(values) {
      const errors: FormErrors<FormValues> = {}
      if (!values.templateId) {
        errors.templateId = i18n.t('common.validations.empty')
      }
      if (values.filename.trim() === '') {
        errors.filename = i18n.t('common.validations.empty')
      }

      return errors
    },
    validateOnChange: true,
    validateOnBlur: false,
    async onSubmit(_values, { setErrors }) {
      const result = await generateFileFromVariables({
        variables: {
          id: previewData?.ats.offerLetterTemplate?.atsFile?.id ?? '0',
          jobApplicationId,
          filename: form.values.filename,
          resourceType: 'application',
          variables: Object.entries(variableValues).map(([name, value]) => ({ name, value })),
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success && !!handled.data?.file) {
        setContent('preview')
        setFinalPreviewFile(handled.data.file)
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.send_offer_letter_modal.from_template.error_generating_file'),
        })
      }

      return handled
    },
  })

  const hasSetDefaultFilename = useRef(false)

  useEffect(() => {
    const fullName = applicationData?.ats.application?.fullName
    if (fullName && !hasSetDefaultFilename.current) {
      hasSetDefaultFilename.current = true
      form.setFieldValue(
        'filename',
        i18n.t('ats.send_offer_letter_modal.from_template.file_name_value', {
          candidate_name: fullName,
        })
      )
    }
  }, [applicationData, form])

  const checkVariableError = (name: string) =>
    (variableValues[name] ?? '').trim() !== '' ? undefined : i18n.t('common.validations.empty')

  const allVariableNames = [
    ...systemVariables.map((v) => v.name),
    ...customVariables.map((v) => v.name),
  ]
  const hasErrors = allVariableNames.some((name) => checkVariableError(name) !== undefined)

  const handleSubmit = () => {
    if (hasErrors || !form.isValid) return

    form.handleSubmit()
  }

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      title={i18n.t('ats.send_offer_letter_modal.from_template.title')}
      position="fullscreen"
      primaryAction={
        content === 'form'
          ? {
              label: i18n.t('common.next'),
              onClick: handleSubmit,
              loading: form.isSubmitting,
            }
          : {
              label: i18n.t('common.send'),
              onClick: () => onSuccess(),
            }
      }
      secondaryAction={
        content === 'form'
          ? {
              label: i18n.t('common.cancel'),
              onClick: () => onClose(),
            }
          : {
              label: i18n.t('common.go_back'),
              onClick: () => {
                setContent('form')
                setFinalPreviewFile(null)
              },
            }
      }
      otherActions={
        content === 'form'
          ? undefined
          : [
              {
                icon: Save,
                label: i18n.t('ats.send_offer_letter_modal.from_template.save_as_draft'),
                onClick: onSaveForLater,
              },
            ]
      }
      disableContentPadding
    >
      <Box height="full">
        {content === 'form' ? (
          <Box flexGrow flexDirection="row" overflowY="hidden">
            <Box flexGrow height="full" overflowY="auto">
              {isPreviewLoading ? (
                <Box
                  height="full"
                  justifyContent="center"
                  alignItems="center"
                  gap="s24"
                  background="grey100"
                >
                  <Spinner />
                </Box>
              ) : previewUrl ? (
                <PdfViewer url={previewUrl} filename={pdfFilename} initialScale="page-width" />
              ) : (
                <Box
                  height="full"
                  justifyContent="center"
                  alignItems="center"
                  gap="s24"
                  background="grey100"
                >
                  <OneEmptyState title="No document preview" emoji="📄" />
                </Box>
              )}
            </Box>
            <Box
              flexNoShrink
              height="full"
              overflowX="hidden"
              border={{ left: { style: 'solid', width: 's1', color: 'grey200' } }}
              style={{ width: '36%' }}
              paddingY="s24"
              paddingX="s16"
              gap="s24"
            >
              <Box gap="s16">
                <Text.Normal size="300" color="textPrimary" weight="semibold">
                  {i18n.t('ats.send_offer_letter_modal.from_template.edit_offer_letter')}
                </Text.Normal>
                <F0Select
                  label="Select template"
                  name="templateId"
                  placeholder={i18n.t('common.select')}
                  onChange={handleTemplateChange}
                  size="md"
                  value={selectedTemplateId ?? undefined}
                  source={templatesSource}
                  mapOptions={(record: Record<string, unknown>) => ({
                    label: String(record.label),
                    value: String(record.value),
                  })}
                  showSearchBox
                  searchBoxPlaceholder={i18n.t('common.search')}
                  searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                  error={form.errors.templateId}
                />
                <Input
                  label={i18n.t('ats.send_offer_letter_modal.from_template.file_name_label')}
                  name="filename"
                  placeholder={i18n.t(
                    'ats.send_offer_letter_modal.from_template.file_name_placeholder'
                  )}
                  size="md"
                  onChange={(value) => form.setFieldValue('filename', value)}
                  value={form.values.filename}
                  error={form.errors.filename}
                />
              </Box>
              {(customVariables.length > 0 || systemVariables.length > 0) && (
                <Box gap="s16">
                  <Text.Normal size="300" color="textPrimary" weight="semibold">
                    {i18n.t('ats.send_offer_letter_modal.from_template.editable_fields')}
                  </Text.Normal>
                  {systemVariables.map((variable) => {
                    if (variable.name === 'salary_amount') {
                      return (
                        <div key={variable.name} onBlur={() => setSalaryTouched(true)}>
                          <Input
                            label={variable.label}
                            name={variable.name}
                            size="md"
                            onChange={(value) => handleVariableChange(variable.name, value)}
                            value={variableValues[variable.name] ?? ''}
                            status={getSalaryWarningStatus({
                              value: variableValues[variable.name],
                              touched: salaryTouched,
                              jobPostingSalary:
                                applicationData?.ats.application?.jobPosting ?? null,
                            })}
                            error={checkVariableError(variable.name)}
                          />
                        </div>
                      )
                    }

                    if (variable.name === 'job_contract_type') {
                      const current = variableValues[variable.name] ?? ''
                      const value = contractTypeOptions.some((o) => o.value === current)
                        ? current
                        : undefined
                      return (
                        <F0Select
                          key={variable.name}
                          label={variable.label}
                          name={variable.name}
                          size="md"
                          placeholder={i18n.t('common.select_basic')}
                          value={value}
                          options={contractTypeOptions}
                          loading={contractTypesLoading}
                          onChange={(v: string) => handleVariableChange(variable.name, v)}
                          error={checkVariableError(variable.name)}
                          showSearchBox
                        />
                      )
                    }

                    if (variable.name === 'job_workplace_type') {
                      const current = variableValues[variable.name] ?? ''
                      const value = workplaceTypeOptions.some((o) => o.value === current)
                        ? current
                        : undefined
                      return (
                        <F0Select
                          key={variable.name}
                          label={variable.label}
                          name={variable.name}
                          size="md"
                          placeholder={i18n.t('common.select_basic')}
                          value={value}
                          options={workplaceTypeOptions}
                          onChange={(v: string) => handleVariableChange(variable.name, v)}
                          error={checkVariableError(variable.name)}
                        />
                      )
                    }

                    if (variable.name === 'team_name') {
                      const current = variableValues[variable.name] ?? ''
                      const value = teamOptions.some((o) => o.value === current)
                        ? current
                        : undefined
                      return (
                        <F0Select
                          key={variable.name}
                          label={variable.label}
                          name={variable.name}
                          size="md"
                          placeholder={i18n.t('common.select_basic')}
                          value={value}
                          options={teamOptions}
                          loading={teamsLoading}
                          onChange={(v: string) => handleVariableChange(variable.name, v)}
                          error={checkVariableError(variable.name)}
                          showSearchBox
                        />
                      )
                    }

                    if (variable.name === 'start_date') {
                      return (
                        <F0DatePicker
                          key={variable.name}
                          label={variable.label}
                          size="md"
                          value={uiDateStringToDatePickerValue(variableValues[variable.name])}
                          onChange={(value) =>
                            handleVariableChange(variable.name, datePickerValueToUiString(value))
                          }
                          error={checkVariableError(variable.name)}
                        />
                      )
                    }

                    return (
                      <Input
                        key={variable.name}
                        label={variable.label}
                        name={variable.name}
                        size="md"
                        onChange={(value) => handleVariableChange(variable.name, value)}
                        value={variableValues[variable.name] ?? ''}
                        error={checkVariableError(variable.name)}
                      />
                    )
                  })}
                  {customVariables.map((variable) => (
                    <Input
                      key={variable.name}
                      label={variable.label}
                      name={variable.name}
                      size="md"
                      onChange={(value) => handleVariableChange(variable.name, value)}
                      value={variableValues[variable.name] ?? ''}
                      error={checkVariableError(variable.name)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <OfferLetterFromTemplateFilePreview
            url={finalPreviewFile?.file.url ?? ''}
            filename={finalPreviewFile?.file.filename ?? ''}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
